// src/components/user/Masivo/MasivoWizard.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, PackageCheck, AlertTriangle, ShieldCheck, PlusCircle } from 'lucide-react';
import DateSelector from './DateSelector';
import FileUploader from './FileUploader';
import ValidationProgress from './ValidationProgress';
import DeliveriesReview from './DeliveriesReview';
import SubmissionProgress from './SubmissionProgress';
import { parseExcelFile, generateExcelFile } from '../../../utils/excelParser';
import { validateAllDeliveries } from '../../../utils/deliveryValidator';
import { DELIVERY_ADDONS, DELIVERY_SERVICE_LEVELS, calculateDeliveryPricing } from '../../../constants/deliveryPricing';
import {
  getRegistrationPreview,
  normalizeIntelligencePhone,
} from '../intelligence/deliveryIntelligenceApi';

const API_URL = process.env.REACT_APP_API_URL;

function getValidIntelligencePhone(row) {
  const hasPhoneError = row.errors?.some(error => error.field === 'phone');
  return hasPhoneError
    ? null
    : normalizeIntelligencePhone(row.data?.['WhatsApp']);
}

function MasivoWizard({
  userId,
  setActiveComponent,
  returnComponent = 'Analytics',
  controlTowerComponent = 'ControlTower',
  title = 'Carga Masiva',
  description = 'Sube un archivo Excel con múltiples entregas',
  successPrimaryLabel = 'Ver Mis Entregas',
  controlTowerLabel = 'Torre de Control',
}) {
  // Step state
  const [currentStep, setCurrentStep] = useState('date'); // date | upload | validating | review | submitting | complete

  // Data state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validatedDeliveries, setValidatedDeliveries] = useState([]);
  const [validDistricts, setValidDistricts] = useState({});
  const [userProducts, setUserProducts] = useState([]);
  const [inventoryOwnerUserId, setInventoryOwnerUserId] = useState(null);
  const [defaultServiceLevelCode, setDefaultServiceLevelCode] = useState('classic');
  const [defaultAddonCodes, setDefaultAddonCodes] = useState([]);
  
  // Submission state
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissionProgress, setSubmissionProgress] = useState({
    total: 0,
    created: 0,
    messagesSent: 0,
    currentStage: 'saving'
  });

  // Error state
  const [error, setError] = useState(null);
  const bulkPricingPreview = calculateDeliveryPricing(defaultServiceLevelCode, defaultAddonCodes);

  // Load districts and user products on mount
  useEffect(() => {
    loadDistricts();
    loadUserProducts();
  }, [userId]);

  async function loadDistricts() {
    try {
      const response = await axios.get(`${API_URL}/api/masivo/districts`);
      setValidDistricts(response.data);
    } catch (err) {
      console.error('Error loading districts:', err);
      // Fallback to empty object
      setValidDistricts({ districts_by_zone: {}, all_districts: [] });
    }
  }

  async function loadUserProducts() {
    try {
      const response = await axios.get(`${API_URL}/api/masivo/user-products/${userId}`);
      setUserProducts(response.data.products || []);
      setInventoryOwnerUserId(response.data.inventory_owner_user_id);
    } catch (err) {
      console.error('Error loading user products:', err);
      setUserProducts([]);
    }
  }

  function handleDateChange(date) {
    setSelectedDate(date);
    // If date changes after file upload, stay on upload step
    if (currentStep !== 'date') {
      setCurrentStep('upload');
      setValidatedDeliveries([]);
    } else if (date) {
      // Move to upload step when date is selected
      setCurrentStep('upload');
    }
  }

  function handleFileSelect(file) {
    setSelectedFile(file);
    setError(null);
    // Start validation
    validateFile(file);
  }

  function handleClearFile() {
    setSelectedFile(null);
    setValidatedDeliveries([]);
    setError(null);
    setCurrentStep('upload');
  }

  async function validateFile(file) {
    try {
      setCurrentStep('validating');
      setError(null);

      // Parse Excel
      const parsed = await parseExcelFile(file);
      
      if (parsed.rows.length === 0) {
        setError('El archivo Excel está vacío');
        setCurrentStep('upload');
        return;
      }

      // Validate all rows
      const validated = validateAllDeliveries(
        parsed.rows, 
        validDistricts
      );

      const normalizedPhones = [
        ...new Set(
          validated.rows
            .map(getValidIntelligencePhone)
            .filter(Boolean)
        ),
      ];

      let enrichedRows;
      if (normalizedPhones.length === 0) {
        enrichedRows = validated.rows.map(row => ({
          ...row,
          intelligence: null,
          intelligenceStatus: 'idle',
        }));
      } else {
        try {
          // One deduplicated request for the complete Excel preview. Historical
          // evidence remains separate from row validation and never blocks it.
          const preview = await getRegistrationPreview(normalizedPhones, {
            context: 'bulk',
            rowCount: validated.rows.length,
          });
          enrichedRows = validated.rows.map(row => {
            const phone = getValidIntelligencePhone(row);
            return {
              ...row,
              intelligence: phone ? preview.results?.[phone] || null : null,
              intelligenceStatus: phone ? 'success' : 'idle',
            };
          });
        } catch (intelligenceError) {
          console.error('Error loading bulk delivery intelligence:', intelligenceError);
          enrichedRows = validated.rows.map(row => ({
            ...row,
            intelligence: null,
            intelligenceStatus: getValidIntelligencePhone(row)
              ? 'error'
              : 'idle',
          }));
        }
      }

      setValidatedDeliveries(enrichedRows);
      setCurrentStep('review');

    } catch (err) {
      console.error('Error validating file:', err);
      setError(err.message || 'Error al procesar el archivo');
      setCurrentStep('upload');
    }
  }

  async function handleUpdateDelivery(updatedDelivery) {
    const phone = getValidIntelligencePhone(updatedDelivery);
    const pendingDelivery = {
      ...updatedDelivery,
      intelligence: null,
      intelligenceStatus: phone ? 'loading' : 'idle',
    };
    setValidatedDeliveries(prev =>
      prev.map(d =>
        d.rowNumber === updatedDelivery.rowNumber ? pendingDelivery : d
      )
    );
    if (!phone) return;

    try {
      const preview = await getRegistrationPreview([phone], {
        context: 'bulk',
        rowCount: 1,
      });
      setValidatedDeliveries(prev => prev.map(d => (
        d.rowNumber === updatedDelivery.rowNumber &&
        getValidIntelligencePhone(d) === phone
          ? {
              ...d,
              intelligence: preview.results?.[phone] || null,
              intelligenceStatus: 'success',
            }
          : d
      )));
    } catch (intelligenceError) {
      console.error('Error refreshing row intelligence:', intelligenceError);
      setValidatedDeliveries(prev => prev.map(d => (
        d.rowNumber === updatedDelivery.rowNumber &&
        getValidIntelligencePhone(d) === phone
          ? { ...d, intelligence: null, intelligenceStatus: 'error' }
          : d
      )));
    }
  }

  function handleDeleteDelivery(rowNumber) {
    setValidatedDeliveries(prev =>
      prev.filter(d => d.rowNumber !== rowNumber)
    );
  }

  function toggleDefaultAddon(addonCode) {
    setDefaultAddonCodes(prev => (
      prev.includes(addonCode)
        ? prev.filter(code => code !== addonCode)
        : [...prev, addonCode]
    ));
  }

  async function handleProceed() {
    try {
      setCurrentStep('submitting');
      setError(null);

      // Get only valid deliveries
      const validDeliveries = validatedDeliveries.filter(d => d.isValid);

      // Prepare data for backend (add selected date to each row)
      const deliveriesData = validDeliveries.map(d => ({
        ...d.data,
        'Servicio': d.data['Servicio'] || defaultServiceLevelCode,
        'Add-ons': d.data['Add-ons'] || defaultAddonCodes.join(','),
        'Fecha': formatDateForBackend(selectedDate)
      }));

      // Generate Excel blob from validated data
      const excelBlob = generateExcelFile(deliveriesData, 'entregas_validadas.xlsx');

      // Prepare form data
      const formData = new FormData();
      formData.append('file', excelBlob, 'entregas_validadas.xlsx');
      formData.append('user_id', userId);
      if (inventoryOwnerUserId) formData.append('expected_inventory_owner_user_id', inventoryOwnerUserId);
      formData.append('default_service_level_code', defaultServiceLevelCode);
      formData.append('default_addon_codes', defaultAddonCodes.join(','));

      // Set initial progress
      setSubmissionProgress({
        total: validDeliveries.length,
        created: 0,
        messagesSent: 0,
        currentStage: 'saving'
      });

      // Submit to backend
      const response = await axios.post(
        `${API_URL}/api/delivery_points/bulk_upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Update progress with results
      setSubmissionProgress({
        total: validDeliveries.length,
        created: response.data.created || 0,
        messagesSent: response.data.send_ok || 0,
        currentStage: 'complete'
      });

      setSubmissionResult(response.data);
      setCurrentStep('complete');

    } catch (err) {
      console.error('Error submitting deliveries:', err);
      setError(err.response?.data?.error || 'Error al crear las entregas');
      setCurrentStep('review');
    }
  }

  function formatDateForBackend(dateString) {
    // Convert YYYY-MM-DD to DD/MM/YYYY for backend
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  function handleReset() {
    setCurrentStep('date');
    setSelectedDate('');
    setSelectedFile(null);
    setValidatedDeliveries([]);
    setSubmissionResult(null);
    setDefaultServiceLevelCode('classic');
    setDefaultAddonCodes([]);
    setError(null);
  }

  function handleBackButton() {
    // If on date selection step, go back to the parent deliveries view
    if (currentStep === 'date') {
      setActiveComponent(returnComponent);
    } else {
      // From any other step, go back to date selection
      setCurrentStep('date');
      setSelectedFile(null);
      setValidatedDeliveries([]);
      setError(null);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handleBackButton}
            className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors duration-200 font-montserrat"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base">
              {currentStep === 'date' ? 'Volver a Mis Entregas' : 'Cambiar Fecha'}
            </span>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-primary font-montserrat">
              {title}
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600 font-montserrat">
              {description}
            </p>
          </div>

          {/* Global error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-red-900 font-montserrat mb-1">
                    Error
                  </h3>
                  <p className="text-sm text-red-700 font-montserrat">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step content */}
          <div className="space-y-6">
            {/* Step 1: Date Selection */}
            {currentStep === 'date' && (
              <DateSelector
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
              />
            )}

            {/* Batch default service */}
            {currentStep !== 'date' && selectedDate && (
              <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat mb-2">
                      Servicio por defecto del lote
                    </h2>
                    <p className="text-sm text-slate-600 font-montserrat">
                      Si el Excel no trae columna Servicio, estas opciones se aplican a todas las entregas.
                    </p>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="text-xs text-slate-500 font-montserrat">Tarifa base</p>
                    <p className="text-lg font-extrabold text-brand-primary font-montserrat">
                      {bulkPricingPreview.totalLabel}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {DELIVERY_SERVICE_LEVELS.map(service => {
                    const selected = defaultServiceLevelCode === service.code;
                    return (
                      <button
                        key={service.code}
                        type="button"
                        disabled={currentStep === 'validating' || currentStep === 'submitting'}
                        onClick={() => setDefaultServiceLevelCode(service.code)}
                        className={`rounded-xl border-2 p-4 text-left transition-all disabled:opacity-60 ${
                          selected
                            ? 'border-brand-primary bg-brand-primary/5 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="flex justify-between gap-2">
                          <p className="text-sm font-bold text-slate-900 font-montserrat">{service.label}</p>
                          <p className="text-sm font-extrabold text-brand-primary font-montserrat">{service.priceLabel}</p>
                        </div>
                        <p className="text-xs text-slate-500 font-montserrat mt-2">{service.shortDescription}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1 text-xs font-bold text-slate-600 font-montserrat mr-1">
                    <PlusCircle className="w-4 h-4 text-brand-green" />
                    Add-ons:
                  </span>
                  {DELIVERY_ADDONS.map(addon => {
                    const active = defaultAddonCodes.includes(addon.code);
                    return (
                      <button
                        key={addon.code}
                        type="button"
                        disabled={currentStep === 'validating' || currentStep === 'submitting'}
                        onClick={() => toggleDefaultAddon(addon.code)}
                        className={`rounded-full border px-3 py-2 text-xs font-semibold font-montserrat transition-all disabled:opacity-60 ${
                          active
                            ? 'border-brand-green bg-brand-green/10 text-brand-green'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-brand-green'
                        }`}
                      >
                        {addon.label} <strong>{addon.priceLabel}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: File Upload */}
            {(currentStep === 'upload' || currentStep === 'validating' || currentStep === 'review' || currentStep === 'submitting' || currentStep === 'complete') && selectedDate && (
              <FileUploader
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                onClearFile={handleClearFile}
                disabled={currentStep === 'validating' || currentStep === 'submitting'}
                userProducts={userProducts}
              />
            )}

            {/* Step 3: Validation Progress */}
            {currentStep === 'validating' && (
              <ValidationProgress
                totalRows={validatedDeliveries.length || 0}
                processedRows={validatedDeliveries.length || 0}
              />
            )}

            {/* Step 4: Review Deliveries */}
            {currentStep === 'review' && validatedDeliveries.length > 0 && (
              <DeliveriesReview
                validatedDeliveries={validatedDeliveries}
                onUpdateDelivery={handleUpdateDelivery}
                onDeleteDelivery={handleDeleteDelivery}
                onProceed={handleProceed}
                onChangeDate={() => setCurrentStep('date')}
                selectedDate={selectedDate}
                validDistricts={validDistricts}
                userProducts={userProducts}
                defaultServiceLevelCode={defaultServiceLevelCode}
                defaultAddonCodes={defaultAddonCodes}
              />
            )}

            {/* Step 5: Submission Progress */}
            {currentStep === 'submitting' && (
              <SubmissionProgress
                total={submissionProgress.total}
                created={submissionProgress.created}
                messagesSent={submissionProgress.messagesSent}
                currentStage={submissionProgress.currentStage}
              />
            )}

            {/* Step 6: Complete */}
            {currentStep === 'complete' && submissionResult && (
              <div className="bg-white rounded-2xl border-2 border-brand-green p-6 sm:p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-green to-brand-primary flex items-center justify-center mb-6">
                    <PackageCheck className="w-10 h-10 text-white" />
                  </div>

                  <h2 className="text-2xl font-extrabold text-brand-green font-montserrat mb-2">
                    ¡Carga Completada!
                  </h2>
                  
                  <p className="text-sm text-slate-600 font-montserrat mb-6">
                    {submissionResult.created} entregas creadas exitosamente
                  </p>

                  {/* Stats */}
                  <div className="w-full max-w-md grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-brand-green/10 rounded-xl p-4 border border-brand-green/30">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Entregas creadas</p>
                      <p className="text-2xl font-bold text-brand-green font-montserrat">
                        {submissionResult.created}
                      </p>
                    </div>
                    <div className="bg-brand-green/10 rounded-xl p-4 border border-brand-green/30">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Mensajes enviados</p>
                      <p className="text-2xl font-bold text-brand-green font-montserrat">
                        {submissionResult.send_ok}
                      </p>
                    </div>
                  </div>

                  {/* Failed messages */}
                  {submissionResult.send_failed_count > 0 && (
                    <div className="w-full max-w-md bg-[#E6B43C]/10 border-l-4 border-[#E6B43C] rounded-lg p-4 mb-6">
                      <p className="text-sm font-semibold text-slate-700 font-montserrat">
                        ⚠️ {submissionResult.send_failed_count} mensaje(s) no se pudieron enviar
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="w-full max-w-md flex flex-col gap-3">
                    <button
                      onClick={() => setActiveComponent(returnComponent)}
                      className="w-full px-4 py-3 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary/90 transition-colors font-montserrat"
                    >
                      {successPrimaryLabel}
                    </button>
                    <button
                      onClick={() => setActiveComponent(controlTowerComponent)}
                      className="w-full px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors font-montserrat"
                    >
                      {controlTowerLabel}
                    </button>
                    <button
                      onClick={handleReset}
                      className="w-full px-4 py-3 text-brand-primary rounded-xl font-semibold hover:bg-brand-primary/5 transition-colors font-montserrat"
                    >
                      Crear Más Entregas
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MasivoWizard;
